import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Card } from '../../components/Atom/card';
import { Breadcrumbs } from '../../components/Atom/breadcumbs';
import { PageHeader } from '../../components/Atom/PageHeader';
import { ResetButton, FilterButton } from '../../components/Atom/Button';
import DateRangePicker from '../../components/Atom/DateRange';
import { notification, Popover, Table, Skeleton, Divider } from 'antd';
import './assets/style.css';
import { useQuery } from 'react-query';
import { handleDateRangeFormat, BanksName, getXAxisLabel } from 'utils/services';
import { formatAmount } from 'utils/services';
import PaymentLineChart from './assets/paymentLineChart';
import Select from 'components/Atom/Select';
import http from '../../utils/http';
import {
  TotalTransactions,
  CapturedTransactions,
  RefundedTransactions,
  PartiallyRefundedTransactions,
  CancelledTransactions,
  FailedTranscations,
  TimeoutTransactions,
  TotalPaymentLinksIcon,
  ActivePaymentLinkIcon,
  CompletePaymentLink,
  ExpiredPaymentLink,
  PendingPaymentLink,
  PendingTransactions,
  OTPIcon,
  AuthorizedTransactions,
  GMV,
  Count,
  Cancelled,
  Failed,
  Paused,
  Subscriber,
  /*  DownArrow,
  UpArrow, */
  MRR,
  ARR,
} from 'assets/icons';
import { TIMEZONES } from 'utils/constant';
import cubejs from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { mapPaymentLinkData, mapTransactionData, mapSubscriptionData, mapTopPlansData, mapTopProductsData, transformToMethodsGatewaysMap } from '../../utils/helper';

import useWindowSize from 'utils/useWindowSize';
import ShortenId from 'components/Atom/ShortenId';
import { ReloadOutlined } from '@ant-design/icons';
import Filter from './assets/filter';
import { cubeApiWrapper } from '../../utils/cubeApiWrapper';
import PaymentTreeMapChart from './assets/paymentTreeMapChart';

const Dashboard = () => {
  const store = useSelector((state) => state.company.store);
  const [dateRange, setDateRange] = useState([]);
  const [queryParams, setQueryParams] = useState(null);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [size] = useWindowSize();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterToggle, setFilterToggle] = useState(false);
  const [paymentGateway, setPaymentGateway] = useState('all');

  const CUBEJS_TOKEN = window?.localStorage?.getItem('cubejs_token');

  const onDateSearch = () => {
    if (dateRange === null || dateRange?.length === 0) {
      notification.error({ message: 'Please enter date range', placement: 'bottomRight' });
      return;
    }
    const timezone = store?.isSelected ? store?.settings?.timezone || TIMEZONES.PKT : null;
    const { start_date, end_date } = handleDateRangeFormat(dateRange, timezone);
    setQueryParams((prev) => ({
      ...prev,
      start_date,
      end_date,
    }));
  };
  const handleDateRange = (range) => {
    if (range === null) {
      setDateRange([]);
      setQueryParams(null);
    } else {
      setDateRange(range);
    }
  };
  const handleOnChangeValue = (e) => {
    if (e === null || e === 'all') {
      setPaymentGateway('all');
    } else {
      setPaymentGateway(e);
    }
  };

  const baseQuery = useMemo(() => {
    if (!shouldFetch || !store?._id || !CUBEJS_TOKEN) return null;
    return {
      filters: [
        ...(store?.isSelected && store?._id
          ? [
              {
                member: 'transactions.store_id',
                operator: 'equals',
                values: [store?._id],
              },
            ]
          : []),
        ...(queryParams?.start_date && queryParams?.end_date
          ? [
              {
                member: 'transactions.updatedAt',
                operator: 'inDateRange',
                values: [queryParams?.start_date, queryParams?.end_date],
              },
            ]
          : []),
        ...(queryParams?.gatewayType
          ? [
              {
                member: 'transactions.gateway',
                operator: 'equals',
                values: [queryParams?.gatewayType],
              },
            ]
          : []),
        ...(queryParams?.paymentMethod
          ? [
              {
                member: 'transactions.payment_method',
                operator: 'equals',
                values: [queryParams?.paymentMethod],
              },
            ]
          : []),
      ],
    };
  }, [shouldFetch, store?._id, store?.isSelected, queryParams, CUBEJS_TOKEN]);

  const getBaseStoreFilter = (dataset) => [
    ...(store?.isSelected && store?._id
      ? [
          {
            member: `${dataset}.store_id`,
            operator: 'equals',
            values: [store?._id],
          },
        ]
      : []),
    ...(queryParams?.start_date && queryParams?.end_date
      ? [
          {
            member: `${dataset}.updatedAt`,
            operator: 'inDateRange',
            values: [queryParams?.start_date, queryParams?.end_date],
          },
        ]
      : []),
  ];

  const getPaymentMethodGatewayFilter = (dataset) => [
    ...(queryParams?.gatewayType
      ? [
          {
            member: `${dataset}.payment_gateway`,
            operator: 'equals',
            values: [queryParams?.gatewayType?.toLowerCase()],
          },
        ]
      : []),
    ...(queryParams?.paymentMethod
      ? [
          {
            member: `${dataset}.payment_method`,
            operator: 'equals',
            values: [queryParams?.paymentMethod?.toLowerCase()],
          },
        ]
      : []),
  ];

  const { resultSet: paymentGatewayPerformanceData, isLoading: isFetchingPaymentGatewayPerformanceData } = useCubeQuery(
    baseQuery && {
      measures: ['transactions.totalTransactions', 'transactions.successfulTransactions', 'transactions.acceptanceRate'],
      timeDimensions: [
        {
          dimension: 'transactions.updatedAt',
          granularity: getXAxisLabel(queryParams)?.value,
        },
      ],
      order: {
        'transactions.updatedAt': 'asc',
      },
      timezone: 'Asia/Karachi',
      filters: [
        ...(paymentGateway !== 'all'
          ? [
              {
                member: 'transactions.gateway',
                operator: 'equals',
                values: [paymentGateway],
              },
            ]
          : []),
        ...getBaseStoreFilter('transactions'),
      ],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const {
    resultSet: paymentReport,
    isLoading: isFetchingPaymentData,
    isFetching,
  } = useCubeQuery(
    baseQuery && {
      ...baseQuery,
      measures: ['transactions.totalTransactions'],
      timeDimensions: [
        {
          dimension: 'transactions.updatedAt',
        },
      ],
      dimensions: ['transactions.status'],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const {
    resultSet: paymentMethodGatewayReport,
    isLoading: isPaymentMethodGatewayData,
    isFetching: isFetchingPaymentMethodGatewayData,
  } = useCubeQuery(
    baseQuery && {
      ...baseQuery,
      measures: ['usage_by_methods_gateways.total_count'],
      timeDimensions: [
        {
          dimension: 'usage_by_methods_gateways.updatedAt',
        },
      ],
      dimensions: ['usage_by_methods_gateways.payment_method', 'usage_by_methods_gateways.payment_gateway'],
      filters: [...getPaymentMethodGatewayFilter('usage_by_methods_gateways'), ...getBaseStoreFilter('usage_by_methods_gateways')],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: capturedTotalReport } = useCubeQuery(
    baseQuery && {
      ...baseQuery,
      measures: ['transactions.capturedTotal'],
      timeDimensions: [
        {
          dimension: 'transactions.updatedAt',
        },
      ],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: paymentRevenueReport, isLoading: isFetchingPaymentRevenueData } = useCubeQuery(
    baseQuery && {
      ...baseQuery,
      measures: ['transactions.totalRevenue'],
      timeDimensions: [
        {
          dimension: 'transactions.updatedAt',
        },
      ],
      dimensions: ['transactions.status'],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: totalGMVData } = useCubeQuery(
    baseQuery && {
      ...baseQuery,
      measures: ['transactions.gmvTotal'],
      timeDimensions: [
        {
          dimension: 'transactions.updatedAt',
        },
      ],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: paymentLinkReport, isLoading: isFetchingPaymentLinkData } = useCubeQuery(
    baseQuery && {
      measures: ['payment_links.count'],
      timeDimensions: [
        {
          dimension: 'payment_links.updatedAt',
        },
      ],
      dimensions: ['payment_links.status'],
      filters: getBaseStoreFilter('payment_links'),
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: tokensReport, isLoading: isFetchingTokensData } = useCubeQuery(
    baseQuery && {
      measures: ['merchant_tokens.activeTokens'],
      filters: getBaseStoreFilter('merchant_tokens'),
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: tokenAttempts } = useCubeQuery(
    baseQuery && {
      measures: ['transactions.tokenAttempts'],
      filters: getBaseStoreFilter('transactions'),
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: totalSubscribers } = useCubeQuery(
    baseQuery && {
      measures: ['subscriptions.totalSubscribers'],
      timeDimensions: [
        {
          dimension: 'subscriptions.updatedAt',
        },
      ],
      filters: getBaseStoreFilter('subscriptions'),
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: subscriptionCount } = useCubeQuery(
    baseQuery && {
      measures: ['subscriptions.subscriptionCount'],
      timeDimensions: [
        {
          dimension: 'subscriptions.updatedAt',
        },
      ],
      filters: getBaseStoreFilter('subscriptions'),
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: subscriptionReport } = useCubeQuery(
    baseQuery && {
      measures: ['subscriptions.subscriptionCount'],
      timeDimensions: [
        {
          dimension: 'subscriptions.updatedAt',
        },
      ],
      dimensions: ['subscriptions.status'],
      filters: [
        {
          member: 'subscriptions.status',
          operator: 'set',
        },
        {
          member: 'subscriptions.status',
          operator: 'notEquals',
          values: [''],
        },
        ...getBaseStoreFilter('subscriptions'),
      ],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: mostSubscribedPlans, isLoading: isFetchingMostSubscribedPlans } = useCubeQuery(
    baseQuery && {
      measures: [
        'subscriptions.subscriptionCount',
        'subscriptions.draftSubscriptions',
        'subscriptions.paidSubscriptions',
        'subscriptions.cancelledSubscriptions',
        'subscriptions.pausedSubscriptions',
        'subscriptions.inactiveSubscriptions',
        'subscriptions.openSubscriptions',
      ],
      dimensions: ['subscriptions.plan_id'],
      order: {
        'subscriptions.subscriptionCount': 'desc',
      },
      filters: [
        {
          member: 'subscriptions.plan_id',
          operator: 'set',
        },
        {
          member: 'subscriptions.plan_id',
          operator: 'notEquals',
          values: [''],
        },
        ...getBaseStoreFilter('subscriptions'),
      ],
      limit: 5,
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: mostSubscribedProducts, isLoading: isFetchingMostSubscribedProducts } = useCubeQuery(
    baseQuery && {
      measures: ['subs_by_products.totalSubscriptionsByProducts'],
      timeDimensions: [
        {
          dimension: 'subs_by_products.updatedAt',
        },
      ],
      dimensions: ['subs_by_products.name', 'subs_by_products.product_id'],
      order: {
        'subs_by_products.totalSubscriptionsByProducts': 'desc',
      },
      filters: getBaseStoreFilter('subs_by_products'),
      limit: 5,
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  const { resultSet: subscriptionRevenue, isLoading: isFetchingSubscriptionRevenue } = useCubeQuery(
    baseQuery && {
      measures: ['transactions.totalRevenue'],
      timeDimensions: [
        {
          dimension: 'transactions.updatedAt',
        },
      ],
      segments: ['transactions.subscription'],
      filters: [
        {
          member: 'transactions.status',
          operator: 'equals',
          values: ['REFUNDED', 'PARTIALLY_REFUNDED', 'CAPTURED'],
        },
        ...getBaseStoreFilter('transactions'),
      ],
    },
    {
      cubeApi: cubeApiWrapper,
    },
  );

  useEffect(() => {
    if (store?._id) {
      setShouldFetch(true);
    } else {
      setShouldFetch(false);
    }
  }, [store?._id]);

  const handleRefresh = () => {
    if (dateRange?.length === 0 && queryParams === null) {
      setShouldFetch(false);
      setTimeout(() => setShouldFetch(true), 0);
    } else {
      setDateRange([]);
      setQueryParams(null);
    }
  };
  const handleFiltersSearch = (data) => {
    const hasActiveFilters = data && Object.keys(data).length > 0 && Object.values(data).some((value) => value !== 'all' && value !== null);

    if (!hasActiveFilters) {
      setQueryParams(null);
      setShouldFetch(false);
      setTimeout(() => setShouldFetch(true), 0);
    } else {
      setQueryParams(data);
      setShouldFetch(false);
      setTimeout(() => setShouldFetch(true), 0);
    }
    setIsFilterModalOpen(false);
  };
  const handleCloseFilterModel = () => {
    setIsFilterModalOpen(!isFilterModalOpen);
  };
  const filterToggler = () => {
    setFilterToggle(true);
  };
  const handleReset = () => {
    setQueryParams(null);
    setDateRange([]);
    setIsFilterModalOpen(false);
  };

  const popOverContent = (
    <div className='popOver'>
      <div className='popover_header mt-2 mb-3 flex items-center gap-2'>
        <span style={{ fontWeight: '700', color: '#262626' }}>Filter</span>
        <div className='flex items-center text-[#000000] gap-2 cursor-pointer' onClick={() => handleReset()}>
          <ReloadOutlined style={{ fontSize: '16px' }} />
          <span style={{ color: '#000000', fontSize: '14px', lineHeight: '24px' }}>Reset All</span>
        </div>
      </div>
      <div className=''>
        <Filter filterToggler={filterToggler} filtersData={queryParams} onSearch={handleFiltersSearch} handleClose={handleCloseFilterModel} />
      </div>
    </div>
  );
  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Dashboard',
      },
    ],
  };
  const CardTransactionData = (() => {
    const mappedData = mapTransactionData(paymentReport?.loadResponse?.results?.[0]?.data || []);
    return [
      {
        title: 'Total',
        icon: <TotalTransactions />,
        value: mappedData.totalTransactions.toString(),
      },
      {
        title: 'Partially Refunded',
        icon: <PartiallyRefundedTransactions />,
        value: mappedData.partially_refunded?.totalTransactions.toString() || '0',
      },
      {
        title: 'Captured',
        icon: <CapturedTransactions />,
        value: mappedData.captured?.totalTransactions.toString() || '0',
      },
      {
        title: 'Authorized',
        icon: <AuthorizedTransactions />,
        value: mappedData.authorized?.totalTransactions.toString() || '0',
      },
      {
        title: 'Cancelled',
        icon: <CancelledTransactions />,
        value: mappedData.cancelled?.totalTransactions.toString() || '0',
      },
      {
        title: 'Failed',
        icon: <FailedTranscations />,
        value: mappedData.failed?.totalTransactions.toString() || '0',
      },
      {
        title: 'Refunded',
        icon: <RefundedTransactions />,
        value: mappedData.refunded?.totalTransactions.toString() || '0',
      },
      {
        title: 'OTP Action Required',
        icon: <OTPIcon />,
        value: mappedData.otp_action_required?.totalTransactions.toString() || '0',
      },
      {
        title: 'Pending',
        icon: <PendingTransactions />,
        value: mappedData.pending?.totalTransactions.toString() || '0',
      },
      {
        title: 'Void',
        icon: <TimeoutTransactions />,
        value: ((mappedData?.timeout?.totalTransactions || 0) + (mappedData?.void?.totalTransactions || 0)).toString(),
      },
    ];
  })();

  const CardGMVData = (() => {
    const mappedData = mapTransactionData(paymentRevenueReport?.loadResponse?.results?.[0]?.data || []);
    const totalGMV = totalGMVData?.loadResponse?.results?.[0]?.data?.[0]?.['transactions.gmvTotal'];
    return [
      {
        title: 'Total',
        icon: <TotalTransactions />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(totalGMV || 0, store?.settings?.currency),
      },
      {
        title: 'Partial Refunds',
        icon: <PartiallyRefundedTransactions />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(mappedData.partially_refunded?.totalRevenue || 0, store?.settings?.currency),
      },
      {
        title: 'Captured',
        icon: <CapturedTransactions />,
        value:
          (store?.settings?.currency || 'PKR') +
          ' ' +
          formatAmount(capturedTotalReport?.loadResponse?.results?.[0]?.data[0]?.['transactions.capturedTotal']?.toString() || 0, store?.settings?.currency),
      },
      {
        title: 'Authorized',
        icon: <AuthorizedTransactions />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(mappedData.authorized?.totalRevenue || 0, store?.settings?.currency),
      },
      {
        title: 'Cancelled',
        icon: <CancelledTransactions />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(mappedData.cancelled?.totalRevenue || 0, store?.settings?.currency),
      },
      {
        title: 'Failed',
        icon: <FailedTranscations />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(mappedData.failed?.totalRevenue || 0, store?.settings?.currency),
      },
      {
        title: 'Refunds',
        icon: <RefundedTransactions />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(mappedData.refunded?.totalRevenue || 0, store?.settings?.currency),
      },
      {
        title: 'OTP Action Required',
        icon: <OTPIcon />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(mappedData.otp_action_required?.totalRevenue || 0, store?.settings?.currency),
      },
      {
        title: 'Pending',
        icon: <PendingTransactions />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount(mappedData.pending?.totalRevenue || 0, store?.settings?.currency),
      },
      {
        title: 'Void',
        icon: <TimeoutTransactions />,
        value: (store?.settings?.currency || 'PKR') + ' ' + formatAmount((mappedData?.timeout?.totalRevenue || 0) + (mappedData?.void?.totalRevenue || 0), store?.settings?.currency),
      },
    ];
  })();
  const paymentLinkCardData = (() => {
    const mappedData = mapPaymentLinkData(paymentLinkReport?.loadResponse?.results?.[0]?.data || []);

    return [
      {
        title: 'Total',
        icon: <TotalPaymentLinksIcon />,
        value: mappedData.total || '0',
      },
      {
        title: 'Active',
        icon: <ActivePaymentLinkIcon />,
        value: mappedData.active || '0',
      },
      {
        title: 'Completed',
        icon: <CompletePaymentLink />,
        value: mappedData?.complete || '0',
      },
      {
        title: 'Expired',
        icon: <ExpiredPaymentLink />,
        value: mappedData.expired || '0',
      },
      {
        title: 'Pending',
        icon: <PendingPaymentLink />,
        value: mappedData.pending || '0',
      },
    ];
  })();
  const tokensCardData = [
    {
      title: 'Active',
      icon: <ActivePaymentLinkIcon />,
      value: tokensReport?.loadResponse?.results?.[0]?.data?.[0]?.['merchant_tokens.activeTokens'] || '0',
    },
    {
      title: 'Attempted',
      icon: <TotalPaymentLinksIcon />,
      value: tokenAttempts?.loadResponse?.results?.[0]?.data?.[0]?.['transactions.tokenAttempts'] || '0',
    },
  ];
  const subscriptionData = mapSubscriptionData(subscriptionReport);
  const subscriptionCardData = [
    {
      title: 'Revenue',
      icon: <GMV />,
      value: formatAmount(subscriptionRevenue?.loadResponse?.results?.[0]?.data?.[0]?.['transactions.totalRevenue']) || '0',
    },
    {
      title: 'Subscribers',
      icon: <Subscriber />,
      value: totalSubscribers?.loadResponse?.results?.[0]?.data?.[0]?.['subscriptions.totalSubscribers'] || '0',
    },
    {
      title: 'Count',
      icon: <Count />,
      value: subscriptionCount?.loadResponse?.results?.[0]?.data?.[0]?.['subscriptions.subscriptionCount'] || '0',
    },
    {
      title: 'Draft',
      icon: <PendingTransactions />,
      value: Number(subscriptionData['draft'] || 0).toLocaleString(),
    },
    {
      title: 'Paid',
      icon: <CapturedTransactions />,
      value: Number(subscriptionData['paid'] || 0).toLocaleString(),
    },
    {
      title: 'Paused',
      icon: <Paused />,
      value: Number(subscriptionData['paused'] || 0).toLocaleString(),
    },
    {
      title: 'Inactive',
      icon: <Failed />,
      value: Number(subscriptionData['inactive'] || 0).toLocaleString(),
    },
    {
      title: 'Cancelled',
      icon: <Failed />,
      value: Number(subscriptionData['cancelled'] || 0).toLocaleString(),
    },
    /* {
      title: 'MRR Run Rate',
      icon: <MRR />,
      value: formatAmount(annualSubscriptionReport?.loadResponse?.results?.[0]?.data?.[0]?.['subscriptions.annualRecurringRevenue']) || '0',
        value: String(`${totalMRR.toLocaleString()}`),
       extraIcon:
        totalMRR < 0 ? (
          <div className='downArrow'>
            <span>
              <DownArrow />
            </span>
            <span style={{ color: '#FF4D4F' }}>{totalMRR ? `${totalMRR.toFixed(2)}%` : '0%'}</span>
          </div>
        ) : (
          <div className='upArrow'>
            <span>
              <UpArrow />
            </span>
            <span style={{ color: '#1EB488' }}>{totalMRR ? `${totalMRR.toFixed(2)}%` : '0%'}</span>
          </div>
        ), 
    },
    {
      title: 'ARR Run Rate',
      icon: <ARR />,
      value: formatAmount(annualSubscriptionReport?.loadResponse?.results?.[0]?.data?.[0]?.['subscriptions.annualRecurringRevenue']) || '0',
    },*/
  ];

  const topPlansData = mapTopPlansData(mostSubscribedPlans?.loadResponse?.results?.[0]?.data || []);
  const planStatisticsColumns = [
    {
      title: 'Plan ID',
      render: (value) => <ShortenId value={value?.plan_id} />,
      key: 'plan_id',
    },
    {
      title: 'Total',
      key: 'total_subscriptions',
      dataIndex: 'total',
    },
    {
      title: 'Paid',
      key: 'paid_subscriptions',
      dataIndex: 'paid',
    },
    {
      title: 'Inactive',
      key: 'inactive_subscriptions',
      dataIndex: 'inactive',
    },
    {
      title: 'Paused',
      key: 'paused_subscriptions',
      dataIndex: 'paused',
    },
    {
      title: 'Cancelled',
      key: 'cancelled_subscriptions',
      dataIndex: 'cancelled',
    },
    {
      title: 'Draft',
      key: 'draft_subscriptions',
      dataIndex: 'draft',
    },
    {
      title: 'Open',
      key: 'open_subscriptions',
      dataIndex: 'open',
    },
  ];
  const topProductsData = mapTopProductsData(mostSubscribedProducts?.loadResponse?.results?.[0]?.data || []);
  const productStatisticsColumns = [
    {
      title: 'Product Name',
      key: 'name',
      dataIndex: 'product_name',
    },
    {
      title: 'Product ID',
      render: (value) => <ShortenId value={value?.product_id} />,
      key: 'product_id',
    },
    {
      title: 'Number of Subcriptions',
      key: 'total_subscriptions',
      dataIndex: 'total',
    },
  ];
  return (
    <div className='card-block'>
      <div className='dashboard-custom-bg p-4'>
        <Breadcrumbs {...breadcrumbProps} />
        <div className='pageHeader'>
          <div className=''>
            <PageHeader label='Dashboard' button={<ResetButton onClick={isFetching ? '' : handleRefresh} loading={isFetching} />} />
          </div>
        </div>
      </div>
      <div className='actionPanel'>
        <div className='headerContainer' style={{ width: '100%' }}>
          <Popover
            className={isFilterModalOpen ? 'filter-active filterPopover' : filterToggle ? 'text-base activeFilterToggler filterPopover' : 'text-base filterPopover'}
            trigger='click'
            placement='bottomLeft'
            content={popOverContent}
            open={isFilterModalOpen}
            destroyTooltipOnHide
            onOpenChange={() => {
              handleCloseFilterModel();
            }}>
            <FilterButton onClick={() => setIsFilterModalOpen(true)}>
              <div className='text-base'>Filter</div>
            </FilterButton>
          </Popover>

          <DateRangePicker className='custom-range mr-3 h-12' onChange={handleDateRange} value={dateRange} onClick={onDateSearch} title={'Apply'} />
        </div>
      </div>
      <div className='mt-4'>
        <h2 className='container-heading mb-4'>PAYMENT STATISTICS</h2>
      </div>
      <div className='card-container'>
        <div className='dashboard-card bg-white'>
          <h3 className='section-title'>Transactions</h3>
          <div className='flex mt-3'>
            <div className='card-columns'>
              {CardTransactionData?.map((obj, index) => (
                <div className='card-wrapper' key={index}>
                  <Card value={obj?.value} icon={obj.icon} label={obj.title} loading={isFetchingPaymentData} />
                  <Divider style={{ padding: '0px', margin: '0px' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className='dashboard-card cardRange ml-4 bg-white'>
          <h3 className='section-title'>GMV</h3>
          <div className='flex mt-3'>
            <div className='card-columns'>
              {CardGMVData?.map((obj, index) => (
                <div className='card-wrapper' key={index}>
                  <Card value={obj?.value} icon={obj.icon} label={obj.title} key={index} loading={isFetchingPaymentRevenueData} />
                  <Divider style={{ padding: '0px', margin: '0px' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className='mt-6 mb-4'>
        <h3 className='container-heading'>PAYMENT GATEWAY ACCEPTANCE RATE</h3>
      </div>
      <div className='charts-container-card bg-white mb-6'>
        <div className='flex justify-end'>
          <div className='flex items-center justify-between gap-2'>
            <div className='w-52'>
              <Select
                defaultValue={paymentGateway ?? 'all'}
                optionsValue={[{ label: 'All', value: 'all' }, ...BanksName]}
                handleChange={(e) => handleOnChangeValue(e.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        <PaymentLineChart
          data={paymentGatewayPerformanceData?.loadResponse?.results?.[0]?.data?.map((item) => ({
            period: item['transactions.updatedAt']?.split('T')[0],
            acceptanceRate: formatAmount(parseFloat(item['transactions.acceptanceRate'])) || '0',
          }))}
          dateRange={queryParams}
        />
      </div>

      <div className='card-container'>
        <div className='dashboard-charts-card bg-white'>
          <h3 className='section-title'>Transactions by Payment Methods</h3>
          <div className='charts-container-card flex mt-3'>
            <PaymentTreeMapChart data={transformToMethodsGatewaysMap(paymentMethodGatewayReport?.loadResponse?.results?.[0]?.data)} type='methods' />
          </div>
        </div>
        <div className='dashboard-charts-card cardRange ml-4 bg-white'>
          <h3 className='section-title'>Transactions by Payment Gateways</h3>
          <div className='charts-container-card flex mt-3'>
            <PaymentTreeMapChart data={transformToMethodsGatewaysMap(paymentMethodGatewayReport?.loadResponse?.results?.[0]?.data)} type='gateways' />
          </div>
        </div>
      </div>

      <div className='mt-6'>
        <h2 className='container-heading mb-4'>SUBSCRIPTIONS</h2>
      </div>
      <div className='links-container-payment flex-wrap bg-white subscription-div' style={{ height: 'auto' }}>
        {subscriptionCardData?.map((obj, index) => (
          <React.Fragment key={index}>
            <Card value={obj?.value} icon={obj?.icon} label={obj?.title} extraIcon={obj?.extraIcon} loading={isFetchingSubscriptionRevenue} />
            {(index + 1) % (size > 768 ? 4 : 2) === 0 && index !== subscriptionCardData.length - 1 && <Divider className='full-width-divider' />}
          </React.Fragment>
        ))}
      </div>
      <div className='mt-6'>
        <h2 className='container-heading mb-4'>PAYMENT LINKS STATISTICS</h2>
      </div>
      <div className='links-container-payment flex flex-wrap bg-white '>
        {paymentLinkCardData.map((obj, index) => (
          <Card value={obj?.value} icon={obj.icon} label={obj.title} key={index} loading={isFetchingPaymentLinkData} />
        ))}
      </div>
      <div className='mt-6'>
        <h2 className='container-heading mb-4'>TOKENS</h2>
      </div>
      <div className='links-container-payment flex flex-wrap bg-white mb-6'>
        {tokensCardData.map((obj, index) => (
          <Card value={obj?.value} icon={obj.icon} label={obj.title} key={index} loading={isFetchingTokensData} />
        ))}
      </div>
      <div className='mt-6 mb-4'>
        <h3 className='container-heading'>TOP PLANS</h3>
      </div>
      <div className='table-container plan-statistics-table mb-6'>
        <Table
          columns={
            isFetchingMostSubscribedPlans
              ? planStatisticsColumns?.map((column) => {
                  return {
                    ...column,
                    render: () => {
                      return <Skeleton active={true} paragraph={false} key={column?.key} />;
                    },
                  };
                })
              : planStatisticsColumns
          }
          dataSource={
            isFetchingMostSubscribedPlans
              ? [...Array(5)].map((_, index) => ({
                  key: `key${index}`,
                }))
              : topPlansData
          }
          pagination={false}
        />
      </div>
      <div className='mt-6 mb-4'>
        <h3 className='container-heading'>TOP PRODUCTS</h3>
      </div>
      <div className='table-container plan-statistics-table mb-6'>
        <Table
          columns={
            isFetchingMostSubscribedProducts
              ? productStatisticsColumns?.map((column) => {
                  return {
                    ...column,
                    render: () => {
                      return <Skeleton active={true} paragraph={false} key={column?.key} />;
                    },
                  };
                })
              : productStatisticsColumns
          }
          dataSource={
            isFetchingMostSubscribedProducts
              ? [...Array(5)].map((_, index) => ({
                  key: `key${index}`,
                }))
              : topProductsData
          }
          pagination={false}
        />
      </div>
    </div>
  );
};

export default Dashboard;
